import React from 'react';
import { NotificationManager } from 'react-notifications'; // https://www.npmjs.com/package/react-notifications
import {
  fetchJson,
  get,
  postAbsolutePath,
  postFormData,
  postJson,
  userAction,
} from '@/util/helpers/ajax-helpers';
import { completed, loading, standardErrorHandler } from './helpers';
import {
  ALERT_MESSAGE,
  ALERTS_ON_OFF,
  CHANGE_TAB,
  CSV_UPLOADED,
  DASHBOARDS_LOADED,
  FEEDS_ON_OFF,
  FLASH_MESSAGE,
  MAP_ENABLED,
  MODAL_SAVE_CLICKED,
  REFRESH_DASHBOARDS_5_MIN_ON_OFF,
  SCHEMA_LOADED,
  SCHEMA_SAVED,
  SET_LOADING_TIMER,
  SHEETS_OTHER_TOOLS_ON_OFF,
  SQL_CHECKED,
  SUGGESTIONS_LOADED,
  TABLES_LOADED,
  VIEWS_DATA_LOADED,
  VIEWS_TAGS_LOADED,
} from './constants';
import ModalSqlEditor from '../components/modals/ModalSqlEditor';
import AlertMessage from '../components/AlertMessage';
import Column from '../util/Column';
import { BILLING, MAP, VIEWS } from '../util/enums/views.constants';

export function uploadCsv(formData, size) {
  return (dispatch) => {
    const MAX_SIZE_MB = 1; /* anything bigger than 1mb and MySQL DBs can quite quickly run out of memory */
    if (size > MAX_SIZE_MB) {
      return dispatch(
        completed(CSV_UPLOADED, {
          error: `File too large: must be less than ${MAX_SIZE_MB}MB in size.`,
        }),
      );
    }
    dispatch(loading(CSV_UPLOADED));
    postFormData('/csv', formData)
      .then((json) => {
        dispatch(completed(CSV_UPLOADED, { json }));
      })
      .catch(standardErrorHandler(CSV_UPLOADED, dispatch));
  };
}

export function enableMap() {
  return (dispatch) => {
    dispatch(loading(MAP_ENABLED));
    postJson('/enableMap')
      .then(function () {
        // done
        dispatch(completed(MAP_ENABLED));
      })
      .catch(standardErrorHandler(MAP_ENABLED, dispatch));
  };
}

export function logout() {
  return () => {
    postAbsolutePath('/logout').then(function () {
      window.location.href = '/';
    });
  };
}

function trimSql(sql) {
  let trimmedSql = sql.trim();
  if (trimmedSql[trimmedSql.length - 1] === ';') {
    trimmedSql = trimmedSql.substring(0, trimmedSql.length - 1);
  }
  return trimmedSql;
}

let latestSql = '';
export function testSql(state, existingLayer) {
  return (dispatch, getState) => {
    dispatch(loading(SQL_CHECKED));
    latestSql = state.sql;
    const trimmedSql = trimSql(state.sql);
    postJson('/checkSql', { _sql: trimmedSql })
      .then(function (json) {
        const globalState = getState();
        const sqlInEditor = globalState.modals.sqlEditorModal.sql;
        if (sqlInEditor !== latestSql) {
          // user has changed SQL, so just ignore response
          return;
        }
        if (trimmedSql !== trimSql(latestSql)) {
          // user has changed SQL (and clicked save), so just ignore this response
          return;
        }
        if (json._columns) {
          // close modal and create layer
          const updatedState = {
            ...state,
            columns: json._columns.map((c) => Column.build(c)),
            sql: trimmedSql,
          };
          dispatch(
            completed(MODAL_SAVE_CLICKED, {
              modal: ModalSqlEditor,
              state: updatedState,
              existingLayer,
              isNewWorld: globalState.datasource.newWorld,
            }),
          );
        } else {
          // show error message
          dispatch(completed(SQL_CHECKED, { sql: trimmedSql, json }));
        }
      })
      .catch(standardErrorHandler(SQL_CHECKED, dispatch));
  };
}

export function loadTables(forceReload, shouldLoadSchema, loadViews = true) {
  return (dispatch) => {
    const path = forceReload ? '/tables?forceReload=true' : '/tables';
    dispatch(loading(TABLES_LOADED));
    fetchJson(path)
      .then(function (json) {
        dispatch(completed(TABLES_LOADED, json));
        // tables have loaded, so can things that depend on it
        if (loadViews) {
          dispatch(loadViewsData()); // this is used to populate datasource.totalSavedViews
        }
        if (shouldLoadSchema) {
          dispatch(loadSchema());
        } else {
          dispatch(loadSuggestions());
        }
      })
      .catch(standardErrorHandler(TABLES_LOADED, dispatch));
  };
}

export function changeTab(tab) {
  return (dispatch, getState) => {
    if (tab === BILLING && getState().datasource.isSelfHosted) {
      return; // can't access billing page from self-hosted version
    }
    dispatch(
      completed(CHANGE_TAB, {
        tab,
        currentTab: getState().datasource.currentTab,
      }),
    );
    userAction(`use-tab-${tab}`, {}, true);
    switch (tab) {
      case VIEWS: {
        dispatch(loadViewsData());
        break;
      }
      case MAP: {
        dispatch(loadSchema());
      }
    }
  };
}

export function loadViewsData() {
  return (dispatch) => {
    dispatch(loading(VIEWS_DATA_LOADED));
    fetchJson('/views/find')
      .then(function (json) {
        dispatch(completed(VIEWS_DATA_LOADED, { json }));
        get('loading available tags', '/views/tags').then((data) => {
          dispatch(completed(VIEWS_TAGS_LOADED, { json: data }));
        });
      })
      .catch(standardErrorHandler(VIEWS_DATA_LOADED, dispatch));
  };
}

function loadSuggestions() {
  return (dispatch) => {
    dispatch(loading(SUGGESTIONS_LOADED));
    fetchJson('/suggestions')
      .then(function (json) {
        dispatch(completed(SUGGESTIONS_LOADED, json));
      })
      .catch(standardErrorHandler(SUGGESTIONS_LOADED, dispatch));
  };
}

let schemaVersion = -1;

export function loadSchema() {
  return (dispatch, getState) => {
    dispatch(loading(SCHEMA_LOADED));
    fetchJson('/schema')
      .then(function (json) {
        schemaVersion = json._version;
        const allTables = getState().datasource.tables;
        dispatch(
          completed(SCHEMA_LOADED, {
            schema: json._schema,
            allTables,
          }),
        );
      })
      .catch(standardErrorHandler(SCHEMA_LOADED, dispatch));
  };
}

let requestNo = 0;

export function saveSchema(
  tables,
  froms,
  tos,
  positions,
  colours,
  pks,
  descriptions,
) {
  return (dispatch) => {
    dispatch(loading(SCHEMA_SAVED));
    dispatch(showFlash(null));
    requestNo = Math.random();
    const currentRequestNo = requestNo;
    // use timer to make sure save waits 5 seconds before saving (in case other changes happen)
    setTimeout(() => {
      if (requestNo !== currentRequestNo) {
        // another request has been issued, so ignoring this request
        return;
      }
      const schemaTables = tables.map((t) => {
        const xy = positions[t.uniqueName()];
        return {
          _table: t,
          _x: !xy ? null : xy[0],
          _y: !xy ? null : xy[1],
          _colour: colours[t.uniqueName()],
          _description: descriptions[t.uniqueName()],
        };
      });
      const fks = froms.map((from, i) => {
        const to = tos[i];
        return { _from: from, _to: to };
      });
      const version = schemaVersion;
      dispatch(
        showFlash(
          <div className="alert alert-info" role="alert">
            Saving...
          </div>,
        ),
      );
      postJson('/schema', {
        _schema: {
          _tables: schemaTables,
          _pks: pks,
          _fks: fks,
        },
        _version: version,
      })
        .then(function (json) {
          if (json._success) {
            schemaVersion = version + 1;
            dispatch(
              showFlash(
                <div className="alert alert-success" role="alert">
                  Saved
                </div>,
                5,
              ),
            );
            dispatch(completed(SCHEMA_SAVED, json));
          } else {
            // handle out-of-date
            dispatch(
              showFlash(
                <div className="alert alert-danger" role="alert">
                  Someone else has updated the diagram. Please{' '}
                  <a href="#" onClick={() => window.location.reload()}>
                    refresh page
                  </a>
                </div>,
              ),
            );
          }
        })
        .catch(standardErrorHandler(SCHEMA_SAVED, dispatch));
    }, 5000);
  };
}

function toggleFeature(path, toggleOn, name, action) {
  return (dispatch) => {
    postJson(path, { on: toggleOn })
      .then(function () {
        const message = `${name} successfully turned ${
          toggleOn ? 'on' : 'off'
        }`;
        NotificationManager.success(message, '', 5000);
        dispatch(completed(action, { on: toggleOn }));
      })
      .catch(() => {
        const failMessage = `Problem turning ${name}${
          toggleOn ? ' on' : ' off'
        }`;
        NotificationManager.error(failMessage, '', 5000);
      });
  };
}

export function turnOnOffDashboardRefreshFiveMins(toggleOn) {
  const name = '5-minute dashboard refresh option';
  return (dispatch) => {
    postJson('/toggleRefreshDashFiveMinutes', { on: toggleOn })
      .then(function () {
        const message = `${name} successfully turned ${
          toggleOn ? 'on' : 'off'
        }`;
        NotificationManager.success(message, '', 5000);
        dispatch(completed(REFRESH_DASHBOARDS_5_MIN_ON_OFF, { on: toggleOn }));
        dispatch(loading(DASHBOARDS_LOADED));
        fetchJson('/dashboards')
          .then(function (json) {
            dispatch(completed(DASHBOARDS_LOADED, json));
          })
          .catch(standardErrorHandler(DASHBOARDS_LOADED, dispatch));
      })
      .catch(() => {
        const failMessage = `Problem turning ${name}${
          toggleOn ? ' on' : ' off'
        }`;
        NotificationManager.error(failMessage, '', 5000);
      });
  };
}

export function turnOnOffFeeds(on) {
  return toggleFeature('/turnOnOffFeeds', on, 'Feeds', FEEDS_ON_OFF);
}

export function turnOnOffAlerts(on) {
  return toggleFeature('/toggleAlertsOnOff', on, 'Alerts', ALERTS_ON_OFF);
}

export function turnOnOffSheestAndOtherTools(on) {
  return toggleFeature(
    '/toggleExportsOnOff',
    on,
    'Exports',
    SHEETS_OTHER_TOOLS_ON_OFF,
  );
}

export function setLoadingTimer(timerCount) {
  return completed(SET_LOADING_TIMER, { timerCount });
}

export function showAlert(message, dismissable = false, buttonName = 'Ok') {
  let payload = {
    content: message,
    dismissable,
    buttonName,
  };
  if (!message) {
    AlertMessage.hide();
    payload = null;
  }
  return completed(ALERT_MESSAGE, { message: payload });
}

export function showFlash(message, seconds = -1) {
  return completed(FLASH_MESSAGE, { message, seconds });
}
